import { APP_INITIALIZER, Provider } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { LoggingService } from '../services/logging.service';

/**
 * Firebase Initialization Checker
 *
 * This initializer ensures that Firebase services (Auth and Firestore) are properly
 * initialized before the Angular application starts. This prevents race conditions
 * where services try to use Firebase before it's ready.
 */
export function initializeFirebase(
  firestore: Firestore,
  auth: Auth,
  loggingService: LoggingService,
): () => Promise<boolean> {
  return async (): Promise<boolean> => {
    try {
      const startTime = Date.now();

      // Verify Firestore is initialized
      if (!firestore) {
        loggingService.logError('❌ Firestore not initialized');
        return false;
      }

      // Verify Auth is initialized
      if (!auth) {
        loggingService.logError('❌ Firebase Auth not initialized');
        return false;
      }

      // Wait for Firebase to be fully ready by checking underlying app
      await new Promise<void>((resolve) => {
        // Give Firebase services time to initialize
        // Increased to 250ms to ensure Firestore is fully initialized before auth calls
        setTimeout(() => resolve(), 250);
      });

      const loadTime = Date.now() - startTime;
      loggingService.logInfo('✅ Firebase services initialized successfully', {
        loadTimeMs: loadTime,
      });

      return true;
    } catch (error) {
      loggingService.logError('❌ Firebase initialization check failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      return false;
    }
  };
}

/**
 * Provider for APP_INITIALIZER
 */
export const FIREBASE_INITIALIZER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initializeFirebase,
  deps: [Firestore, Auth, LoggingService],
  multi: true,
};
